// The exported code uses Tailwind CSS. Install Tailwind CSS in your dev environment to ensure all styles work.
import React from 'react';
import { Button, Card } from 'antd';
import {
  DownloadOutlined,
  PlayCircleOutlined,
  StarOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
const App: React.FC = () => {
  const [activeFeature, setActiveFeature] = React.useState('ai-editing');
  const [activeCategory, setActiveCategory] = React.useState('Ai 视频翻译');
  
  // 将typewriter逻辑移到组件顶层
  React.useEffect(() => {
    const texts = [
      '视频综合处理，自动批量剪辑',
      '视频翻译，助力短剧出海',
      '万能资源嗅探，一键下载媒体',
      '二创去重，自动批量处理',
      '提文案&加字幕，免费批量处理',
    ];
    
    let timeoutId: number;
    let currentIndex = 0;
    const typewriterElement = document.getElementById('typewriter');
    const updateText = () => {
      if (!typewriterElement) return;
      currentIndex = (currentIndex + 1) % texts.length;
      typewriterElement.textContent = texts[currentIndex];
      timeoutId = window.setTimeout(updateText, 3000);
    };
    
    timeoutId = window.setTimeout(updateText, 3000);
    if (typewriterElement) {
      typewriterElement.textContent = texts[0];
    }
    
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, []);
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
@keyframes float {
0%, 100% { transform: translateY(0px); }
50% { transform: translateY(-10px); }
}
@keyframes glow {
0%, 100% { box-shadow: 0 0 20px rgba(147, 51, 234, 0.3); }
50% { box-shadow: 0 0 30px rgba(147, 51, 234, 0.6); }
}
.animate-float {
animation: float 3s ease-in-out infinite;
}
.animate-glow {
animation: glow 2s ease-in-out infinite;
}
.!rounded-button {
border-radius: 12px !important;
}
body {
min-height: 1024px;
}
`;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const coreFeatures = [
    {
      id: 'ai-editing',
      title: '对话式 AI 剪辑',
      description:
        '基于 DeepSeek 的对话式 AI 剪辑工具，支持多轮对话修改剪辑效果',
      icon: 'fas fa-comments',
      color: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
    },
    {
      id: 'video-processing',
      title: '视频综合处理',
      description: '涵盖 30 多种剪辑手法，如裁剪、滤镜、模糊遮挡、变速、扫光等',
      icon: 'fas fa-video',
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
    },
    {
      id: 'ai-translation',
      title: 'AI 视频翻译',
      description:
        '支持 20 多种语言互译，具备配音翻译、双语字幕、人声分离等功能',
      icon: 'fas fa-language',
      color: 'text-green-400',
      bgColor: 'bg-green-500/10',
    },
    {
      id: 'resource-sniffing',
      title: '万能资源嗅探',
      description: '能获取各类客户端或网页的媒体数据，支持一键下载',
      icon: 'fas fa-search',
      color: 'text-orange-400',
      bgColor: 'bg-orange-500/10',
    },
    {
      id: 'deduplication',
      title: '二创去重',
      description: '预设多级去重手法，有初级、中级、高级等去重方案',
      icon: 'fas fa-shield-alt',
      color: 'text-pink-400',
      bgColor: 'bg-pink-500/10',
    },
    {
      id: 'subtitle-extraction',
      title: '提文案 & 加字幕',
      description: '能批量提取视频文案并添加字幕，精准识别文案',
      icon: 'fas fa-closed-captioning',
      color: 'text-cyan-400',
      bgColor: 'bg-cyan-500/10',
    },
  ];
  const versionUpdates = [
    {
      version: 'v1.4.3',
      date: '2025-01-15',
      features: ['修复声音克隆功能', '优化批量解析识别', '提升处理稳定性'],
    },
    {
      version: 'v1.4.0',
      date: '2024-12-20',
      features: ['新增多模式图文成片', '高清修复功能', 'AI 声音克隆优化'],
    },
  ];
  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black w-full relative overflow-x-hidden">
      {/* 背景网格和光效 */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-[120px]"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-[120px]"></div>
      <div className="max-w-[1440px] mx-auto relative">
        {/* 导航栏 */}
        <nav className="flex items-center justify-between px-20 py-6 relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center">
              <img
                src="http://file.1foo.com/2025/08/20/0fd9142ccdc8abaa0cb1ef7987b57fd7.png"
                alt="肥猫剪辑大师"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <span className="text-2xl font-bold text-white">
                肥猫剪辑大师
              </span>
              <p className="text-sm text-gray-400 mt-1">
                聚合式 AI 媒体处理工具
              </p>
            </div>
          </div>
          <div className="flex items-center gap-8">
            <a
              href="#features"
              className="text-gray-300 hover:text-white transition-colors cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                document
                  .getElementById('features')
                  ?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              功能特点
            </a>
            <a
              href="#updates"
              className="text-gray-300 hover:text-white transition-colors cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                document
                  .getElementById('updates')
                  ?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              版本对比
            </a>
            <a
              href="#download"
              className="text-gray-300 hover:text-white transition-colors cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                document
                  .getElementById('download')
                  ?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              下载安装
            </a>
            <a
              href="#contact"
              className="text-gray-300 hover:text-white transition-colors cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                document
                  .getElementById('contact')
                  ?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              联系我们
            </a>
            <Button
              type="primary"
              size="large"
              className="!bg-gradient-to-r !from-purple-500 !to-pink-500 !border-none !rounded-button whitespace-nowrap cursor-pointer"
              icon={<DownloadOutlined />}
              onClick={() => {
                document
                  .getElementById('download')
                  ?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              立即下载
            </Button>
          </div>
        </nav>
        {/* Hero 区域 - 左文右图布局 */}
        <div className="relative min-h-[700px] px-20 py-20">
          <div className="flex items-center justify-between gap-20 h-full">
            {/* 左侧文字区域 */}
            <div className="relative z-10 w-[600px] flex-shrink-0">
              <h1 className="text-6xl font-bold text-white mb-6 leading-tight">
                <span>肥猫剪辑大师</span>
                <span
                  id="typewriter"
                  className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500 mt-2"
                >
                  视频综合处理，自动批量剪辑
                </span>
              </h1>
              <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                免费无广告，可批量处理、支持 GPU、持续更新中...
              </p>
              <div className="flex items-center gap-6 mb-12">
                <Button
                  type="primary"
                  size="large"
                  className="!bg-gradient-to-r !from-purple-500 !to-pink-500 !border-none !rounded-button !px-8 !py-6 !h-auto !text-lg whitespace-nowrap cursor-pointer animate-glow"
                  icon={<DownloadOutlined />}
                  onClick={() => {
                    document
                      .getElementById('download')
                      ?.scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  免费下载 v1.4.3
                </Button>
                <Button
                  size="large"
                  className="!bg-white/10 !border-white/30 !text-white !rounded-button !px-8 !py-6 !h-auto !text-lg whitespace-nowrap cursor-pointer hover:!bg-white/20"
                  icon={<PlayCircleOutlined />}
                >
                  观看演示
                </Button>
              </div>
              <div className="flex items-center gap-8 text-sm text-gray-300">
                <div className="flex items-center gap-2">
                  <i className="fas fa-check-circle text-green-400"></i>
                  <span>免费无广告</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fas fa-check-circle text-green-400"></i>
                  <span>支持 GPU 加速</span>
                </div>
                <div className="flex items-center gap-2">
                  <i className="fas fa-check-circle text-green-400"></i>
                  <span>批量处理</span>
                </div>
              </div>
            </div>
            {/* 右侧产品展示区域 */}
            <div className="relative z-10 w-[800px] flex-shrink-0">
              <div className="relative">
                <div className="w-full rounded-2xl overflow-hidden shadow-2xl" style={{ paddingTop: '56.25%' }}>
                  <video
                    className="absolute top-0 left-0 w-full h-full object-contain"
                    controls
                    autoPlay
                    muted
                    loop
                    playsInline
                  >
                    <source
                      src="http://cloud.video.taobao.com/play/u/null/p/1/e/6/t/1/531311463554.mp4"
                      type="video/mp4"
                    />
                    Your browser does not support the video tag.
                  </video>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none"></div>
                </div>
                {/* 装饰性光效 */}
                <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full blur-[50px] opacity-50"></div>
                <div className="absolute -top-4 -left-4 w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full blur-[40px] opacity-40"></div>
              </div>
            </div>
          </div>
        </div>
        {/* 版本信息区域 */}
        <section id="updates" className="px-12 py-20 bg-gray-800/20">
          <div className="relative max-w-[1320px] mx-auto mb-20">
            <div className="absolute left-0 right-0 top-1/2 h-0.5 bg-gradient-to-r from-purple-500 to-pink-500"></div>
            <div className="flex justify-between items-start gap-4 pt-8">
              {/* v1.4.3 */}
              <div className="relative w-[250px]">
                <div className="absolute left-1/2 -translate-x-1/2 -top-2 w-4 h-4 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"></div>
                <div className="text-center pt-6">
                  <div className="flex flex-col items-center gap-1 mb-3">
                    <span className="text-xl font-semibold text-purple-400">
                      v1.4.3
                    </span>
                    <span className="text-base text-gray-400">2025-08-17</span>
                  </div>
                  <p className="text-base text-gray-300 leading-relaxed">
                    修复 声音克隆0秒、批量解析识别、去水印下载问题
                  </p>
                </div>
              </div>
              {/* v1.4.2 */}
              <div className="relative w-[250px]">
                <div className="absolute left-1/2 -translate-x-1/2 -top-2 w-4 h-4 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"></div>
                <div className="text-center pt-6">
                  <div className="flex flex-col items-center gap-1 mb-3">
                    <span className="text-lg font-semibold text-purple-400">
                      v1.4.2
                    </span>
                    <span className="text-base text-gray-400">2025-08-15</span>
                  </div>
                  <p className="text-base text-gray-300 leading-relaxed">
                    修复导出命名、下载失败、综合处理等问题
                  </p>
                </div>
              </div>
              {/* v1.4.0 */}
              <div className="relative w-[250px]">
                <div className="absolute left-1/2 -translate-x-1/2 -top-2 w-4 h-4 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"></div>
                <div className="text-center pt-6">
                  <div className="flex flex-col items-center gap-1 mb-3">
                    <span className="text-lg font-semibold text-purple-400">
                      v1.4.0
                    </span>
                    <span className="text-base text-gray-400">2025-08-12</span>
                  </div>
                  <div className="space-y-1">
                    <p className="text-base text-gray-300 leading-relaxed">
                      • 新增 多模式图文成片、高清修复功能
                    </p>
                    <p className="text-base text-gray-300 leading-relaxed">
                      • 大面积功能优化，提高稳定性
                    </p>
                    <p className="text-base text-gray-300 leading-relaxed">
                      • 加入 会员中心、代理分销方案
                    </p>
                  </div>
                </div>
              </div>
              {/* v1.3.5 */}
              <div className="relative w-[250px]">
                <div className="absolute left-1/2 -translate-x-1/2 -top-2 w-4 h-4 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"></div>
                <div className="text-center pt-6">
                  <div className="flex flex-col items-center gap-1 mb-3">
                    <span className="text-lg font-semibold text-purple-400">
                      v1.3.5
                    </span>
                    <span className="text-base text-gray-400">2025-07-15</span>
                  </div>
                  <div className="space-y-1">
                    <p className="text-base text-gray-300 leading-relaxed">
                      • 新增声音克隆、人声分离功能
                    </p>
                    <p className="text-base text-gray-300 leading-relaxed">
                      • 修复用户反馈问题及优化
                    </p>
                  </div>
                </div>
              </div>
              {/* v1.3.4 */}
              <div className="relative w-[250px]">
                <div className="absolute left-1/2 -translate-x-1/2 -top-2 w-4 h-4 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"></div>
                <div className="text-center pt-6">
                  <div className="flex flex-col items-center gap-1 mb-3">
                    <span className="text-lg font-semibold text-purple-400">
                      v1.3.4
                    </span>
                    <span className="text-base text-gray-400">2025-06-06</span>
                  </div>
                  <p className="text-base text-gray-300 leading-relaxed">
                    修复 视频综合处理 的页面显示问题和报错问题
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* 核心功能展示 */}
        <section id="features" className="px-20 py-20 bg-black/40">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-6">
              产品功能
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">
                {' '}
                列表
              </span>
            </h2>
          </div>
          {/* 功能卡片网格 */}
          <div className="grid grid-cols-4 gap-6 mb-20">
            <Card className="!bg-black/50 !border-gray-700/50 hover:!border-purple-500/50 transition-all duration-300">
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-3">
                  Ai 文本剪视频
                </h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-2 py-0.5 bg-indigo-500/20 text-indigo-400 rounded-full text-xs">
                    快速编辑视频
                  </span>
                  <span className="px-2 py-0.5 bg-indigo-500/20 text-indigo-400 rounded-full text-xs">
                    文本剪视频
                  </span>
                </div>
                <p className="text-gray-300 leading-relaxed mb-4 text-sm">
                  通过 Ai
                  语音识别出视频的每个段落内容，根据文字内容选择要删除的视频片段。
                </p>
                <Button className="!bg-indigo-500/20 !border-indigo-500/50 !text-indigo-400 !rounded-button whitespace-nowrap">
                  查看
                </Button>
              </div>
            </Card>
            <Card className="!bg-black/50 !border-gray-700/50 hover:!border-purple-500/50 transition-all duration-300">
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-3">
                  视频转图文
                </h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-2 py-0.5 bg-rose-500/20 text-rose-400 rounded-full text-xs">
                    语音识别
                  </span>
                  <span className="px-2 py-0.5 bg-rose-500/20 text-rose-400 rounded-full text-xs">
                    视频截图
                  </span>
                </div>
                <p className="text-gray-300 leading-relaxed mb-4 text-sm">
                  将视频的语音文本和画面截图一起提取出来，方便后续的内容再加工。
                </p>
                <Button className="!bg-rose-500/20 !border-rose-500/50 !text-rose-400 !rounded-button whitespace-nowrap">
                  查看
                </Button>
              </div>
            </Card>
            <Card className="!bg-black/50 !border-gray-700/50 hover:!border-purple-500/50 transition-all duration-300">
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-3">
                  提文案 & 加字幕
                </h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded-full text-xs">
                    文案提取
                  </span>
                  <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded-full text-xs">
                    字幕添加
                  </span>
                  <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded-full text-xs">
                    原字幕遮挡
                  </span>
                </div>
                <p className="text-gray-300 leading-relaxed mb-4 text-sm">
                  批量本地提取视频文案并加字幕。精准识别视频文案，多种字幕样式可选，支持模糊遮挡视频原字幕。
                </p>
                <Button className="!bg-amber-500/20 !border-amber-500/50 !text-amber-400 !rounded-button whitespace-nowrap">
                  查看
                </Button>
              </div>
            </Card>
            <Card className="!bg-black/50 !border-gray-700/50 hover:!border-purple-500/50 transition-all duration-300">
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-3">
                  多模式合成
                </h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-2 py-0.5 bg-lime-500/20 text-lime-400 rounded-full text-xs">
                    好物混剪合成
                  </span>
                  <span className="px-2 py-0.5 bg-lime-500/20 text-lime-400 rounded-full text-xs">
                    视频合成
                  </span>
                  <span className="px-2 py-0.5 bg-lime-500/20 text-lime-400 rounded-full text-xs">
                    字幕音频合成
                  </span>
                </div>
                <p className="text-gray-300 leading-relaxed mb-4 text-sm">
                  多镜头好物随机合成、传统多视频合成、字幕|音频|标题合成到视频，支持批量处理。
                </p>
                <Button className="!bg-lime-500/20 !border-lime-500/50 !text-lime-400 !rounded-button whitespace-nowrap">
                  查看
                </Button>
              </div>
            </Card>
            <Card className="!bg-black/50 !border-gray-700/50 hover:!border-purple-500/50 transition-all duration-300">
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-3">
                  批量去水印解析
                </h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-2 py-0.5 bg-teal-500/20 text-teal-400 rounded-full text-xs">
                    媒体下载
                  </span>
                  <span className="px-2 py-0.5 bg-teal-500/20 text-teal-400 rounded-full text-xs">
                    批量解析
                  </span>
                </div>
                <p className="text-gray-300 leading-relaxed mb-4 text-sm">
                  支持众多主流媒体平台的视频无水印解析下载，可批量提交，一键保存全部视频，高清下载。
                </p>
                <Button className="!bg-teal-500/20 !border-teal-500/50 !text-teal-400 !rounded-button whitespace-nowrap">
                  查看
                </Button>
              </div>
            </Card>
            <Card className="!bg-black/50 !border-gray-700/50 hover:!border-purple-500/50 transition-all duration-300">
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-3">二创去重</h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="px-2 py-0.5 bg-fuchsia-500/20 text-fuchsia-400 rounded-full text-xs">
                    二创去重
                  </span>
                  <span className="px-2 py-0.5 bg-fuchsia-500/20 text-fuchsia-400 rounded-full text-xs">
                    批量处理
                  </span>
                </div>
                <p className="text-gray-300 leading-relaxed mb-4 text-sm">
                  预设多级去重手法，一键批量去重。内置初级、中级、高级去重方案，支持批量导入视频并去重操作。
                </p>
                <Button className="!bg-fuchsia-500/20 !border-fuchsia-500/50 !text-fuchsia-400 !rounded-button whitespace-nowrap">
                  查看
                </Button>
              </div>
            </Card>
            <Card className="!bg-black/50 !border-gray-700/50 hover:!border-purple-500/50 transition-all duration-300">
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-3">
                  视频综合处理
                </h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {['批量处理', '专业二创', '随机剪辑', '单项加工'].map(
                    (tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full text-xs"
                      >
                        {tag}
                      </span>
                    )
                  )}
                </div>
                <p className="text-gray-300 leading-relaxed mb-4 text-sm">
                  30+种剪辑手法，如：裁剪、滤镜、模糊遮挡、变速、扫光、抽帧补帧等常见操作，可批量可存模板。
                </p>
                <Button className="!bg-purple-500/20 !border-purple-500/50 !text-purple-400 !rounded-button">
                  查看
                </Button>
              </div>
            </Card>
            <Card className="!bg-black/50 !border-gray-700/50 hover:!border-purple-500/50 transition-all duration-300">
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-3">
                  Ai视频翻译
                </h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {['短剧出海', '海外二创', '视频重配音'].map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <p className="text-gray-300 leading-relaxed mb-4 text-sm">
                  支持20+种语言互译，配音翻译+双语字幕+人声分离，Ai精确优化内容，更好的音画对其和视频观感。
                </p>
                <Button className="!bg-blue-500/20 !border-blue-500/50 !text-blue-400 !rounded-button">
                  查看
                </Button>
              </div>
            </Card>
            <Card className="!bg-black/50 !border-gray-700/50 hover:!border-purple-500/50 transition-all duration-300">
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-3">
                  万能资源嗅探
                </h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {['媒体下载', '视频号下载', '网页嗅探'].map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <p className="text-gray-300 leading-relaxed mb-4 text-sm">
                  轻松拿到各类客户端或网页的媒体数据。图片、视频、直播流，只需正常浏览，即可捕捉并一键下载。
                </p>
                <Button className="!bg-green-500/20 !border-green-500/50 !text-green-400 !rounded-button">
                  查看
                </Button>
              </div>
            </Card>
            <Card className="!bg-black/50 !border-gray-700/50 hover:!border-purple-500/50 transition-all duration-300">
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-3">
                  Ai声音克隆
                </h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {['声音克隆', '网红配音', '第一人称解说', '文本配音'].map(
                    (tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 bg-pink-500/20 text-pink-400 rounded-full text-xs"
                      >
                        {tag}
                      </span>
                    )
                  )}
                </div>
                <p className="text-gray-300 leading-relaxed mb-4 text-sm">
                  高保真快速AI声音克隆，内置3000+网红音色，10秒语音，快速克隆。
                </p>
                <Button className="!bg-pink-500/20 !border-pink-500/50 !text-pink-400 !rounded-button">
                  查看
                </Button>
              </div>
            </Card>
            <Card className="!bg-black/50 !border-gray-700/50 hover:!border-purple-500/50 transition-all duration-300">
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-3">
                  Ai视频转图文笔记
                </h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {['图文笔记制作', '视频转文章', '视频总结'].map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded-full text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <p className="text-gray-300 leading-relaxed mb-4 text-sm">
                  通过Ai将视频转换为图文笔记，突出重点，图文并茂，带目录和链接跳转。
                </p>
                <Button className="!bg-orange-500/20 !border-orange-500/50 !text-orange-400 !rounded-button">
                  查看
                </Button>
              </div>
            </Card>
            <Card className="!bg-black/50 !border-gray-700/50 hover:!border-purple-500/50 transition-all duration-300">
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-3">
                  Ai人声分离
                </h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {['人声提取', '伴奏提取', '批量分离人声'].map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 rounded-full text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <p className="text-gray-300 leading-relaxed mb-4 text-sm">
                  采用最新的Ai人声分离模型，高精准的实现分离人声和伴奏声。
                </p>
                <Button className="!bg-cyan-500/20 !border-cyan-500/50 !text-cyan-400 !rounded-button">
                  查看
                </Button>
              </div>
            </Card>
            <Card className="!bg-black/50 !border-gray-700/50 hover:!border-purple-500/50 transition-all duration-300">
              <div className="p-6">
                <h3 className="text-xl font-bold text-white mb-3">
                  Ai图文成片
                </h3>
                <div className="flex flex-wrap gap-2 mb-4">
                  {['文案成片', '图文成片', '笔记成片'].map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-0.5 bg-violet-500/20 text-violet-400 rounded-full text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <p className="text-gray-300 leading-relaxed mb-4 text-sm">
                  你可以只提供文案或者图文，Ai自动包装生成视频，支持多种输入方案。
                </p>
                <Button className="!bg-violet-500/20 !border-violet-500/50 !text-violet-400 !rounded-button">
                  查看
                </Button>
              </div>
            </Card>
          </div>
          {/* 功能详细展示 */}
          <div className="bg-black/40 rounded-3xl p-12 border border-gray-700/30">
            <div className="flex gap-8 items-center">
              <div
                className="flex flex-col gap-6 justify-center"
                role="tablist"
                aria-label="产品功能列表"
              >
                {coreFeatures.map((feature) => (
                  <a
                    onClick={(e) => {
                      e.preventDefault();
                      if (feature.id === 'ai-editing') {
                        window.open('https://wwql.lanzout.com/b00rob796b')
                      }
                    }}
                    data-readdy={
                      feature.id === 'ai-editing' ? 'true' : undefined
                    }
                    href={feature.id === 'ai-editing' 
                      ? 'https://wwql.lanzout.com/b00rob796b' 
                      : '#'}
                  >
                    <button
                      key={feature.id}
                      onClick={() => {
                        setActiveFeature(feature.id);
                        const imageElement = document.querySelector('.feature-image') as HTMLElement;
                        if (imageElement) {
                          imageElement.style.opacity = '0';
                          imageElement.style.transform =
                            'scale(0.95) translateY(10px)';
                          setTimeout(() => {
                            imageElement.style.opacity = '1';
                            imageElement.style.transform =
                              'scale(1) translateY(0)';
                          }, 50);
                        }
                      }}
                      role="tab"
                      aria-selected={activeFeature === feature.id}
                      aria-controls={`${feature.id}-panel`}
                      id={`${feature.id}-tab`}
                      className={`px-6 py-3 rounded-xl text-sm font-medium transition-all duration-300 whitespace-nowrap !rounded-button transform hover:scale-105 hover:shadow-lg hover:shadow-purple-500/20 ${
                        activeFeature === feature.id
                          ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white scale-105'
                          : 'bg-gray-800/50 text-gray-400 hover:text-white'
                      }`}
                    >
                      {feature.title}
                    </button>
                  </a>
                ))}
              </div>
              <div className="relative bg-gray-800/30 rounded-2xl p-8 border border-gray-700/30 transition-all duration-500 hover:border-purple-500/50 hover:shadow-lg hover:shadow-purple-500/20">
                <div className="aspect-video w-full overflow-hidden rounded-xl transition-transform duration-500">
                  {activeFeature === 'ai-editing' && (
                    <img
                      src="http://file.1foo.com/2025/08/27/bdcf5db2a84433fc3fbeb72de9867946.png"
                      alt="对话式 AI 剪辑"
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                  )}
                  {activeFeature === 'video-processing' && (
                    <img
                      src="http://file.1foo.com/2025/08/27/866425086174fa25515a48b4611bafe7.png"
                      alt="视频综合处理"
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                  )}
                  {activeFeature === 'ai-translation' && (
                    <img
                      src="http://file.1foo.com/2025/08/27/4ae72300c39297b878a418903825bf99.png"
                      alt="AI视频翻译"
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                  )}
                  {activeFeature === 'resource-sniffing' && (
                    <img
                      src="http://file.1foo.com/2025/08/27/318a5b5bdf171b7f26b83454d2366281.png"
                      alt="万能资源嗅探"
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                  )}
                  {activeFeature === 'deduplication' && (
                    <img
                      src="http://file.1foo.com/2025/08/27/93e35691efe0eefdb7b2888bbb4b7908.png"
                      alt="二创去重"
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                  )}
                  {activeFeature === 'subtitle-extraction' && (
                    <img
                      src="http://file.1foo.com/2025/08/27/488e5c80f44539b34ebe13027f38255c.png"
                      alt="提文案&加字幕"
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                  )}
                </div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full blur-[50px] opacity-50"></div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-16 items-center mt-12">
              <div>
                <h3 className="text-3xl font-bold text-white mb-6">
                  {coreFeatures.find((f) => f.id === activeFeature)?.title}
                </h3>
                {activeFeature === 'ai-editing' && (
                  <div className="space-y-6">
                    <p className="text-lg text-gray-300 leading-relaxed">
                      基于 DeepSeek 的对话式 AI
                      剪辑工具，让视频剪辑变得像聊天一样简单。
                      支持多轮对话修改剪辑效果，可保存优质指令模板，实现批量处理。
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <CheckCircleOutlined className="text-purple-400" />
                        <span className="text-white">
                          多轮对话交互，精确理解需求
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircleOutlined className="text-purple-400" />
                        <span className="text-white">
                          保存优质指令，一键复用模板
                        </span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircleOutlined className="text-purple-400" />
                        <span className="text-white">
                          批量处理视频，提升工作效率
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                {activeFeature === 'video-processing' && (
                  <div className="space-y-6">
                    <p className="text-lg text-gray-300 leading-relaxed">
                      涵盖 30
                      多种专业剪辑手法，包括裁剪、滤镜、模糊遮挡、变速、扫光、
                      抽帧补帧等，支持批量处理并可保存为模板。
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-3">
                        <CheckCircleOutlined className="text-blue-400" />
                        <span className="text-white">视频裁剪合并</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircleOutlined className="text-blue-400" />
                        <span className="text-white">滤镜特效处理</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircleOutlined className="text-blue-400" />
                        <span className="text-white">变速调节</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircleOutlined className="text-blue-400" />
                        <span className="text-white">抽帧补帧</span>
                      </div>
                    </div>
                  </div>
                )}
                {activeFeature === 'ai-translation' && (
                  <div className="space-y-6">
                    <p className="text-lg text-gray-300 leading-relaxed">
                      支持 20
                      多种语言互译，具备配音翻译、双语字幕、人声分离等功能，
                      优化音画同步和观感，助力短剧出海等场景。
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <CheckCircleOutlined className="text-green-400" />
                        <span className="text-white">20+ 种语言互译</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircleOutlined className="text-green-400" />
                        <span className="text-white">AI 配音翻译</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircleOutlined className="text-green-400" />
                        <span className="text-white">双语字幕生成</span>
                      </div>
                    </div>
                  </div>
                )}
                {activeFeature === 'resource-sniffing' && (
                  <div className="space-y-6">
                    <p className="text-lg text-gray-300 leading-relaxed">
                      万能资源嗅探工具，能获取各类客户端或网页的媒体数据，
                      包括图片、视频、直播流等，支持一键下载。
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <CheckCircleOutlined className="text-orange-400" />
                        <span className="text-white">网页媒体资源嗅探</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircleOutlined className="text-orange-400" />
                        <span className="text-white">视频号内容下载</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircleOutlined className="text-orange-400" />
                        <span className="text-white">直播流录制</span>
                      </div>
                    </div>
                  </div>
                )}
                {activeFeature === 'deduplication' && (
                  <div className="space-y-6">
                    <p className="text-lg text-gray-300 leading-relaxed">
                      预设多级去重手法，提供初级、中级、高级等不同程度的去重方案，
                      可批量导入视频进行去重操作。
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <CheckCircleOutlined className="text-pink-400" />
                        <span className="text-white">多级去重算法</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircleOutlined className="text-pink-400" />
                        <span className="text-white">批量去重处理</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircleOutlined className="text-pink-400" />
                        <span className="text-white">智能识别相似内容</span>
                      </div>
                    </div>
                  </div>
                )}
                {activeFeature === 'subtitle-extraction' && (
                  <div className="space-y-6">
                    <p className="text-lg text-gray-300 leading-relaxed">
                      能批量提取视频文案并添加字幕，精准识别文案内容，
                      提供多种字幕样式，还支持模糊遮挡原字幕。
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <CheckCircleOutlined className="text-cyan-400" />
                        <span className="text-white">智能文案提取</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircleOutlined className="text-cyan-400" />
                        <span className="text-white">多样字幕样式</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircleOutlined className="text-cyan-400" />
                        <span className="text-white">原字幕遮挡</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="relative">
                <div className="aspect-video bg-black/60 rounded-2xl overflow-hidden border border-gray-700/30">
                  <img
                    src={`https://readdy.ai/api/search-image?query=professional%20video%20editing%20software%20interface%20with%20modern%20dark%20theme%20and%20purple%20accents%20showing%20timeline%20and%20effects%20panel%20clean%20UI%20design&width=600&height=400&seq=${activeFeature}&orientation=landscape`}
                    alt="功能演示"
                    className="w-full h-full object-cover object-top feature-image transition-all duration-300"
                    style={{
                      opacity: 1,
                      transform: 'scale(1) translateY(0)',
                    }}
                  />
                </div>
                <div className="absolute -top-4 -right-4 w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full animate-float"></div>
              </div>
            </div>
          </div>
        </section>
        {/* 版本信息区域 */}
        <section className="px-20 py-20 bg-black/60">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-bold text-white mb-6">
                  原生开发的短视频批量剪辑
                </h2>
                <p className="text-xl text-gray-300 leading-relaxed mb-8">
                  快速生成大规模的宣传视频，利用原生开发技术和自有的视频去重优化算法，助力视频效果提升
                </p>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-film text-purple-400 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">
                        一键视频批量混剪
                      </h3>
                      <p className="text-gray-400">大量视频成片，高效创作</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-code-branch text-blue-400 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">
                        深度算法去重
                      </h3>
                      <p className="text-gray-400">批量制作原创视频</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-pink-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-magic text-pink-400 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">
                        全方位编辑功能
                      </h3>
                      <p className="text-gray-400">
                        支持音视频裁剪、文字、特效、滤镜、贴纸、转场、字幕、配乐等
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-tags text-green-400 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">
                        私有素材匹配
                      </h3>
                      <p className="text-gray-400">智能标签分析</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src="http://file.1foo.com/2025/08/20/ee283dc11a5647ac1dc61fbf3c486a46.webp"
                    alt="批量剪辑功能展示"
                    className="w-full object-cover"
                  />
                </div>
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full blur-[50px] opacity-50"></div>
                <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full blur-[60px] opacity-40"></div>
              </div>
            </div>
          </div>
        </section>
        {/* 下载区域 */}
        <section id="download" className="px-20 py-20 bg-black/40">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-6">
              立即
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">
                {' '}
                下载
              </span>
            </h2>
            <p className="text-xl text-gray-400">
              选择适合您系统的版本，开始智能剪辑之旅
            </p>
          </div>
          <div className="grid grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
            <Card className="!bg-gray-800/50 !border-gray-700/50 relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-1 text-sm">
                推荐
              </div>
              <div className="p-8 text-center">
                <div className="w-24 h-24 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-8">
                  <i className="fab fa-windows text-4xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">
                  Windows 版本
                </h3>
                <p className="text-gray-300 mb-6 text-lg">
                  适用于 Windows 10/11 系统
                </p>
                <div className="space-y-3 mb-8 text-base text-gray-300">
                  <p>版本：v1.4.3</p>
                  <p>大小：156 MB</p>
                  <p>更新时间：2025-01-15</p>
                </div>
                <Button
                  type="primary"
                  size="large"
                  block
                  className="!bg-gradient-to-r !from-purple-400 !to-pink-400 !border-none !rounded-button !h-14 !text-lg whitespace-nowrap cursor-pointer hover:!from-purple-500 hover:!to-pink-500 transition-colors shadow-lg shadow-purple-500/20"
                  icon={<DownloadOutlined />}
                  onClick={() =>
                    window.open('https://wwql.lanzout.com/b00rob796b')
                  }
                >
                  下载 Windows 版
                </Button>
              </div>
            </Card>
            <Card className="!bg-gray-800/50 !border-gray-700/50">
              <div className="p-8 text-center">
                <div className="w-20 h-20 bg-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className="fab fa-apple text-3xl text-gray-400"></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">
                  macOS 版本
                </h3>
                <p className="text-gray-400 mb-6">适用于 macOS 10.15 及以上</p>
                <div className="space-y-2 mb-8 text-sm text-gray-400">
                  <p>版本：v1.4.0</p>
                  <p>大小：168 MB</p>
                  <p>更新时间：2024-12-20</p>
                </div>
                <Button
                  size="large"
                  block
                  className="!bg-white/10 !border-white/30 !text-white !rounded-button !h-12 whitespace-nowrap cursor-pointer hover:!bg-white/20"
                  icon={<DownloadOutlined />}
                  onClick={() =>
                    window.open('https://wwql.lanzout.com/b00rob796b')
                  }
                >
                  下载 macOS 版
                </Button>
              </div>
            </Card>
          </div>
          {/* 系统要求 */}
          <div className="bg-black/40 rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-6 text-center">
              系统要求
            </h3>
            <div className="grid grid-cols-2 gap-8">
              <div>
                <h4 className="text-lg font-semibold text-white mb-4">
                  Windows 系统
                </h4>
                <div className="space-y-2 text-gray-400">
                  <p>• Windows 10 (64位) 或更高版本</p>
                  <p>• 4GB 内存 (推荐 8GB 或更多)</p>
                  <p>• 2GB 可用磁盘空间</p>
                  <p>• 支持 DirectX 11 的显卡</p>
                </div>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-white mb-4">
                  macOS 系统
                </h4>
                <div className="space-y-2 text-gray-400">
                  <p>• macOS 10.15 或更高版本</p>
                  <p>• 4GB 内存 (推荐 8GB 或更多)</p>
                  <p>• 2GB 可用磁盘空间</p>
                  <p>• Metal 支持的显卡</p>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* 用户评价 */}
        <section className="px-20 py-20 bg-black/60">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-6">
              用户
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">
                {' '}
                评价
              </span>
            </h2>
            <p className="text-xl text-gray-400">来自真实用户的使用体验分享</p>
          </div>
          <div className="grid grid-cols-3 gap-8">
            {[
              {
                name: '小李',
                role: '短视频创作者',
                avatar: 'L',
                content:
                  '老猫剪辑的 AI 对话功能太强了！以前需要手动调整半天的效果，现在直接说一句话就搞定了。批量处理功能让我的工作效率提升了好几倍。',
                rating: 5,
              },
              {
                name: '张导演',
                role: '影视后期制作',
                avatar: 'Z',
                content:
                  '作为专业的后期制作人员，我对工具的要求很高。老猫剪辑的功能很全面，特别是 AI 翻译和去重功能，在处理国际项目时非常有用。',
                rating: 5,
              },
              {
                name: '王老师',
                role: '教育工作者',
                avatar: 'W',
                content:
                  '用老猫剪辑制作教学视频非常方便，自动字幕功能准确率很高，多语言翻译帮助我制作了很多国际化的教学内容。强烈推荐！',
                rating: 5,
              },
            ].map((review, index) => (
              <Card key={index} className="!bg-gray-800/50 !border-gray-700/50">
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-2xl text-white font-bold">
                      {review.avatar}
                    </div>
                    <div>
                      <h4 className="text-white font-semibold">
                        {review.name}
                      </h4>
                      <p className="text-gray-400">{review.role}</p>
                    </div>
                  </div>
                  <p className="text-gray-300 leading-relaxed mb-4 text-sm">
                    {review.content}
                  </p>
                  <div className="flex items-center gap-1">
                    {[...Array(review.rating)].map((_, i) => (
                      <StarOutlined key={i} className="text-yellow-400" />
                    ))}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>
        {/* 联系我们 */}
        <section id="contact" className="px-20 py-20">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-6">
              联系
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-500">
                {' '}
                我们
              </span>
            </h2>
            <p className="text-xl text-gray-400">
              有任何问题或建议，欢迎随时与我们联系
            </p>
          </div>
          <div className="grid grid-cols-2 gap-16 max-w-6xl mx-auto">
            <div className="space-y-8">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                  <i className="fas fa-envelope text-purple-400 text-xl"></i>
                </div>
                <div>
                  <h4 className="text-white font-semibold">邮箱联系</h4>
                  <p className="text-gray-400">help@1foo.com</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                  <i className="fab fa-qq text-blue-400 text-xl"></i>
                </div>
                <div>
                  <h4 className="text-white font-semibold">QQ 群</h4>
                  <p className="text-gray-400">老猫剪辑交流群：1047345657</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                  <i className="fab fa-weixin text-green-400 text-xl"></i>
                </div>
                <div>
                  <h4 className="text-white font-semibold">微信客服</h4>
                  <p className="text-gray-400">Min75336</p>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-center justify-center">
              <div className="w-48 h-48 bg-white p-4 rounded-2xl mb-6">
                <img
                  src="http://file.1foo.com/2025/08/22/500d6c8dd131746bb892d3f31572eed1.png"
                  alt="客服二维码"
                  className="w-full h-full object-cover rounded-xl"
                />
              </div>
              <p className="text-gray-400 text-center">
                扫码添加客服微信
                <br />
                获取技术支持
              </p>
            </div>
          </div>
        </section>
        {/* 页脚 */}
        <footer className="px-20 py-12 border-t border-gray-700/30">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center">
                <img
                  src="http://file.1foo.com/2025/08/20/0fd9142ccdc8abaa0cb1ef7987b57fd7.png"
                  alt="肥猫剪辑大师"
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="text-xl font-bold text-white">肥猫剪辑大师</span>
            </div>
            <div className="flex items-center gap-8 text-gray-400">
              <span>© 2025 肥猫剪辑大师. 保留所有权利.</span>
              <span className="text-gray-400">
                增值电信业务许可证 粤B2-20240709{' '}
                <a
                  href="https://beian.miit.gov.cn/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-purple-400 transition-colors"
                >
                  粤ICP备20065976号-8
                </a>{' '}
                Copyright ©2022-25 All Rights
              </span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};
export default App;
